/**
 * Documents à valider : liste cross-clients des documents en attente,
 * actions rapides Valider / Refuser (saisie motif).
 */
import type { DocumentStatus } from '@bcl/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import { listAdminDocuments, reviewDocument } from '@/features/admin/admin-api';
import { extractErrorMessage } from '@/features/auth/error-message';

const statusOptions: { value: DocumentStatus; label: string }[] = [
  { value: 'en_attente', label: 'En attente' },
  { value: 'valide', label: 'Validés' },
  { value: 'refuse', label: 'Refusés' },
  { value: 'a_completer', label: 'À compléter' },
];

export function AdminDocumentsPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<DocumentStatus>('en_attente');
  const [reason, setReason] = useState<Record<string, string>>({});

  const docs = useQuery({
    queryKey: ['admin', 'documents', statusFilter],
    queryFn: () => listAdminDocuments(statusFilter),
  });

  const reviewMut = useMutation({
    mutationFn: ({
      id,
      action,
      reasonText,
    }: {
      id: string;
      action: 'valide' | 'refuse' | 'a_completer';
      reasonText?: string;
    }) => reviewDocument(id, { status: action, reason: reasonText }),
    onSuccess: () => {
      toast.success('Document mis à jour');
      qc.invalidateQueries({ queryKey: ['admin', 'documents'] });
      qc.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  return (
    <DashboardLayout variant="admin" title="Documents">
      <Card>
        <CardHeader>
          <CardTitle>Documents</CardTitle>
          <CardDescription>
            {docs.data ? `${docs.data.length} documents` : 'Chargement…'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {statusOptions.map((o) => (
              <Button
                key={o.value}
                size="sm"
                variant={statusFilter === o.value ? 'default' : 'outline'}
                onClick={() => setStatusFilter(o.value)}
              >
                {o.label}
              </Button>
            ))}
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fichier</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Uploadé le</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {docs.data && docs.data.length > 0 ? (
                docs.data.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell className="font-medium">{d.filename}</TableCell>
                    <TableCell className="capitalize">
                      {d.documentType.replace(/_/g, ' ')}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          d.status === 'valide'
                            ? 'bg-green-100 text-green-800'
                            : d.status === 'refuse'
                              ? 'bg-red-100 text-red-800'
                              : d.status === 'a_completer'
                                ? 'bg-orange-100 text-orange-800'
                                : 'bg-blue-100 text-blue-800'
                        }
                      >
                        {d.status.replace('_', ' ')}
                      </Badge>
                      {d.reviewReason && (
                        <p className="mt-1 text-xs text-gray-500">{d.reviewReason}</p>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {new Date(d.uploadedAt).toLocaleDateString('fr-FR')}
                    </TableCell>
                    <TableCell className="text-right">
                      {statusFilter === 'en_attente' && (
                        <div className="flex items-center justify-end gap-2">
                          <Input
                            placeholder="Motif refus / complément"
                            value={reason[d.id] ?? ''}
                            onChange={(e) =>
                              setReason((prev) => ({ ...prev, [d.id]: e.target.value }))
                            }
                            className="h-8 w-48"
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-green-700 hover:bg-green-50"
                            onClick={() =>
                              reviewMut.mutate({ id: d.id, action: 'valide' })
                            }
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-700 hover:bg-red-50"
                            onClick={() =>
                              reviewMut.mutate({
                                id: d.id,
                                action: 'refuse',
                                reasonText: reason[d.id] ?? 'Non précisé',
                              })
                            }
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-sm text-gray-500">
                    Aucun document.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
